import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { apiGet, getApiErrorMessage } from "@/api";
import { tokenQuery } from "./query-options";
import type { QueryKey } from "@tanstack/react-query";

export interface ProfileData {
  isAdmin?: boolean;
  preferred_locale?: string | null;
  preferred_currency?: string | null;
  timezone?: string | null;
  analytics_consent_accepted?: boolean | null;
}

export interface AbuseFlag {
  id: string;
  userId: string;
  reason: string;
  resolvedAt: string | null;
  createdAt: string;
}

export interface WebhookFailure {
  id: string;
  eventType: string;
  stripeEventId: string;
  errorMessage: string;
  createdAt: string;
}

export interface FeedbackItem {
  id: string;
  userId: string;
  category: string;
  content: string;
  source: string;
  status: string;
  createdAt: string;
}

export interface UnpaidAffiliate {
  userId: string;
  email: string | null;
  unpaidCents: number;
}

export function profileQueryOptions(token: string | null) {
  return tokenQuery(["profile"], token, async (t) => {
    const res = await apiGet<{ profile?: ProfileData }>("/me", t);
    if (!res.ok) throw new Error(getApiErrorMessage(res, "Profil konnte nicht geladen werden"));
    return res.data.profile ?? {};
  });
}

export function useProfile() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(profileQueryOptions(token));
}

export function abuseFlagsQueryOptions(token: string | null) {
  return tokenQuery(["admin-abuse-flags"], token, async (t) => {
    const res = await apiGet<{ flags?: AbuseFlag[] }>("/admin/abuse-flags", t);
    if (!res.ok) throw new Error(getApiErrorMessage(res, "Abuse-Flags konnten nicht geladen werden"));
    return res.data.flags ?? [];
  });
}

export function useAbuseFlags() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(abuseFlagsQueryOptions(token));
}

export function webhookFailuresQueryOptions(token: string | null) {
  return tokenQuery(["admin-webhook-failures"], token, async (t) => {
    const res = await apiGet<{ failures?: WebhookFailure[] }>("/admin/webhook-failures", t);
    if (!res.ok) throw new Error(getApiErrorMessage(res, "Webhook-Failures konnten nicht geladen werden"));
    return res.data.failures ?? [];
  });
}

export function useWebhookFailures() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(webhookFailuresQueryOptions(token));
}

export function adminFeedbackQueryOptions(token: string | null) {
  return tokenQuery(["admin-feedback"], token, async (t) => {
    const res = await apiGet<{ items?: FeedbackItem[] }>("/admin/feedback", t);
    if (!res.ok) throw new Error(getApiErrorMessage(res, "Feedback konnte nicht geladen werden"));
    return res.data.items ?? [];
  });
}

export function useAdminFeedback() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(adminFeedbackQueryOptions(token));
}

export function unpaidAffiliatesQueryOptions(token: string | null) {
  return tokenQuery(["admin-unpaid-affiliates"], token, async (t) => {
    const res = await apiGet<{ affiliates?: UnpaidAffiliate[] }>("/admin/affiliate/unpaid", t);
    if (!res.ok) throw new Error(getApiErrorMessage(res, "Affiliates konnten nicht geladen werden"));
    return res.data.affiliates ?? [];
  });
}

export function useUnpaidAffiliates() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(unpaidAffiliatesQueryOptions(token));
}

/**
 * These two are cleared from ROUTES rather than from a mutation hook — `/me` is PATCHed inline
 * in settings.tsx, and the affiliate payout is POSTed inline in admin.tsx. They wrote their keys
 * out by hand as `["profile"]` and `["admin-unpaid-affiliates"]`, which worked only because
 * `invalidateQueries` matches on a key PREFIX. That is a correct-by-accident arrangement: it
 * survives the token being appended, and it stops surviving the moment a prefix is renamed or a
 * parameter is added ahead of the token. Exported here so a route clears the same key the query
 * registered, and so `query-wiring.test.ts` sees them at all.
 */
export function profileInvalidateKeys(token: string | null): readonly QueryKey[] {
  return [profileQueryOptions(token).queryKey];
}

export function unpaidAffiliatesInvalidateKeys(token: string | null): readonly QueryKey[] {
  return [unpaidAffiliatesQueryOptions(token).queryKey];
}
